import { BoundingSphere } from "@novorender/data-js-api";

import type { ObjectDataAccess } from "dataAccess";
import { getSelectionBoundingSphere, MAX_SELECTION_BOUNDS_OBJECT_IDS } from "utils/objectData";
import { IdList } from "utils/selectionSet";

/** Delay before showing the loading indicator, to avoid flicker on fast lookups. */
const FOCUS_BOUNDING_SPHERE_LOADER_DELAY = 1000;
/** Give up resolving bounding boxes after this long and fall back to focusing on the view center. */
const FOCUS_BOUNDING_SPHERE_TIMEOUT = 3000;

/**
 * Resolve the combined bounding sphere of a selection to focus the camera on, while keeping the
 * axis sphere responsive (BM-10168):
 * - Skips entirely when the selection has more than `MAX_SELECTION_BOUNDS_OBJECT_IDS` objects.
 * - Falls back to `undefined` if resolving takes longer than a few seconds, regardless of whether
 *   the underlying lookup honors the abort signal.
 * - Reports loading state, delayed slightly to avoid flicker, via `onLoadingChange`.
 *
 * Returns `undefined` when no sphere could be resolved (too large, timed out or no bounds found),
 * in which case the caller should fall back to the no-selection behaviour.
 */
export async function getSelectionFocusBoundingSphere({
    ids,
    abortSignal,
    dataAccess,
    flipCoords,
    onLoadingChange,
}: {
    ids: IdList;
    abortSignal: AbortSignal;
    dataAccess: ObjectDataAccess;
    flipCoords?: boolean;
    onLoadingChange?: (loading: boolean) => void;
}): Promise<BoundingSphere | undefined> {
    if (ids.length === 0 || abortSignal.aborted) {
        return undefined;
    }

    if (ids.length > MAX_SELECTION_BOUNDS_OBJECT_IDS) {
        return undefined;
    }

    const searchAbortController = new AbortController();
    const abortSearch = () => searchAbortController.abort();
    abortSignal.addEventListener("abort", abortSearch);

    let loaderTimer: ReturnType<typeof setTimeout> | undefined = setTimeout(() => {
        loaderTimer = undefined;
        onLoadingChange?.(true);
    }, FOCUS_BOUNDING_SPHERE_LOADER_DELAY);

    // Race the lookup against the search being aborted (which resolves `undefined`). Aborting the
    // search isn't enough on its own: the backends have branches that don't promptly
    // honor the abort signal, so without the race the helper could stay stuck (keeping the loader up
    // and wasting work) well past the timeout or after the caller aborted. Both the timeout and the
    // caller's `abortSignal` funnel through `abortSearch`, so listening on the internal
    // `searchAbortController` covers both cases - and, being short-lived, avoids leaving listeners
    // accumulating on a long-lived signal reused across calls.
    const timeoutTimer = setTimeout(abortSearch, FOCUS_BOUNDING_SPHERE_TIMEOUT);
    const aborted = new Promise<undefined>((resolve) => {
        searchAbortController.signal.addEventListener("abort", () => resolve(undefined), { once: true });
    });

    try {
        return (
            (await Promise.race([
                getSelectionBoundingSphere({
                    ids,
                    abortSignal: searchAbortController.signal,
                    dataAccess,
                    flipCoords,
                }),
                aborted,
            ])) ?? undefined
        );
    } finally {
        clearTimeout(timeoutTimer);
        abortSignal.removeEventListener("abort", abortSearch);
        if (loaderTimer !== undefined) {
            clearTimeout(loaderTimer);
        } else {
            onLoadingChange?.(false);
        }
    }
}
